import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../lib/utils';
import JobCard from '../../components/JobCard';
import { Button } from '../../components/ui/button';
import { Loader2, Ear, ArrowRight, Search } from 'lucide-react';

const FACETS = [
  { key: 'all', label: 'Current Opportunities', params: {} },
  { key: 'remote', label: 'Remote Opportunities', params: { is_remote: 'true' } },
  { key: 'asl-required', label: 'ASL Required', params: { search: 'ASL required' } },
  { key: 'asl-preferred', label: 'ASL Preferred', params: { search: 'ASL preferred' } },
  { key: 'advocacy', label: 'Advocacy & Case Management', params: { search: 'advocate' } },
  { key: 'mental-health', label: 'Mental Health', params: { search: 'mental health' } },
  { key: 'voc-rehab', label: 'Vocational Rehabilitation', params: { search: 'vocational rehabilitation' } },
  { key: 'schools', label: 'Schools & Universities', params: { search: 'school' } },
];

const DeafHardOfHearingJobs = () => {
  const [facet, setFacet] = useState('all');
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Deaf & Hard of Hearing Social Work Jobs | ASL, RCD & Deaf Services | INASW';
    const desc = 'Find social work, case management, advocacy and vocational rehabilitation jobs serving the Deaf and Hard of Hearing community — including ASL, Deaf mental health, Deaf education and Rehabilitation Counselor for the Deaf (RCD) roles.';
    let tag = document.querySelector('meta[name="description"]');
    if (!tag) { tag = document.createElement('meta'); tag.setAttribute('name', 'description'); document.head.appendChild(tag); }
    const prev = tag.getAttribute('content');
    tag.setAttribute('content', desc);
    return () => { if (prev) tag.setAttribute('content', prev); };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const active = FACETS.find((f) => f.key === facet) || FACETS[0];
      const params = new URLSearchParams({ populations: 'deaf-hoh', limit: '50', ...active.params });
      const r = await axios.get(`${API_URL}/jobs?${params.toString()}`);
      setJobs(r.data.jobs || []);
      setTotal(r.data.total || 0);
    } catch (e) { console.error('Deaf/HoH jobs load failed:', e); } finally { setLoading(false); }
  }, [facet]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-gray-50 pt-20" data-testid="deaf-hoh-jobs-page">
      {/* Hero */}
      <section className="bg-primary text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 md:py-16">
          <div className="flex items-center gap-2 text-secondary font-semibold text-sm mb-3">
            <Ear className="w-5 h-5" /> Special Populations &amp; Skills
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl font-bold leading-tight max-w-3xl">
            Social Work Jobs Serving the Deaf &amp; Hard of Hearing Community
          </h1>
          <p className="mt-4 text-white/85 text-base md:text-lg max-w-2xl">
            Roles in Deaf services, Deaf mental health, advocacy, case management, and vocational rehabilitation —
            including ASL-required positions and Rehabilitation Counselor for the Deaf (RCD). Organized by who you want to serve, not just your license.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/jobs?populations=deaf-hoh" data-testid="see-all-deaf-jobs">
              <Button className="bg-secondary hover:bg-secondary-hover text-white h-11">
                Browse in full search <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Facet chips */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="deaf-facets">
          {FACETS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFacet(f.key)}
              data-testid={`facet-${f.key}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${facet === f.key ? 'bg-primary text-white' : 'bg-white text-gray-700 border border-gray-200 hover:border-primary'}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <p className="text-sm text-gray-500 mb-4" data-testid="deaf-jobs-count">
          {loading ? 'Loading…' : `${total.toLocaleString()} ${total === 1 ? 'job' : 'jobs'} found`}
        </p>

        {loading ? (
          <div className="py-16 text-center text-gray-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : jobs.length === 0 ? (
          <div className="bg-white border border-gray-100 rounded-2xl p-10 text-center" data-testid="deaf-jobs-empty">
            <Search className="w-8 h-8 text-gray-300 mx-auto mb-3" />
            <h3 className="font-serif text-xl font-bold text-primary mb-2">No matching roles right now</h3>
            <p className="text-gray-500 max-w-md mx-auto text-sm">
              New Deaf &amp; Hard of Hearing roles are added as employers post and as we import them nationwide.
              Save this search or set a job alert so you're the first to know.
            </p>
            <Link to="/jobs?populations=deaf-hoh" className="mt-4 inline-block">
              <Button variant="outline" className="h-10">Set up a job alert</Button>
            </Link>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {jobs.map((job) => (
              <JobCard key={job.job_id} job={job} />
            ))}
          </div>
        )}

        {/* SEO context block */}
        <div className="mt-12 bg-white border border-gray-100 rounded-2xl p-6 md:p-8 prose prose-sm max-w-none">
          <h2 className="font-serif text-2xl font-bold text-primary">About Deaf &amp; Hard of Hearing social work roles</h2>
          <p className="text-gray-600">
            Social workers who serve the Deaf and Hard of Hearing community go by many titles — Social Worker, Case Manager,
            Advocate, Behavioral Health Clinician, Disability Services Coordinator, and in states like Kentucky,
            <strong> Rehabilitation Counselor for the Deaf (RCD)</strong>. Searching only &ldquo;social worker&rdquo; misses many of these roles,
            which is why this page matches terms like Deaf, Hard of Hearing, D/HH, ASL, American Sign Language, Deaf Services,
            Deaf Mental Health, Deaf Education, and Deaf/Blind across every job in our nationwide database.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DeafHardOfHearingJobs;
